import { useNavigate } from "react-router-dom";
import { BarChart3, Clock, Copy, ExternalLink, KeyRound, Send, Trash2 } from "lucide-react";

const timeAgo = (iso) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  if (s < 604800) return `${Math.round(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export function ProspectRow({ p, origin, onCopy, onDelete, onSendLink, onChangeCode }) {
  const nav = useNavigate();
  const url = `${origin}/p/${p.slug}`;
  return (
    <li className="border border-ink-900/10 rounded-md bg-white/55 hover:bg-white/80 transition-colors" data-testid={`prospect-${p.slug}`}>
      <div className="p-5 md:p-6 grid grid-cols-1 md:grid-cols-12 gap-5 items-center">
        <div className="md:col-span-4 flex items-center gap-4">
          {p.logo_url ? (
            <img src={p.logo_url} alt="" className="h-10 w-10 object-contain bg-cream-50 rounded p-1" />
          ) : (
            <div className="h-10 w-10 rounded bg-cream-200 grid place-items-center serif text-lg text-sun-500">
              {(p.company_name || "?")[0]}
            </div>
          )}
          <div className="min-w-0">
            <div className="serif text-xl truncate">{p.company_name}</div>
            {p.contact_name && (
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-ink-900/55 mt-0.5 truncate">
                {p.contact_name}{p.contact_email ? ` · ${p.contact_email}` : ""}
              </div>
            )}
            <div className="mono text-[10px] uppercase tracking-[0.18em] mt-1 flex items-center gap-1.5"
                 style={{ color: p.last_viewed_at ? "#B45309" : "rgba(15,30,46,0.35)" }}
                 data-testid={`last-viewed-${p.slug}`}
                 title={p.last_viewed_at ? new Date(p.last_viewed_at).toLocaleString() : "No visits yet"}>
              <Clock className="h-3 w-3 flex-shrink-0" />
              {p.last_viewed_at ? `Last viewed ${timeAgo(p.last_viewed_at)}` : "Never viewed"}
            </div>
          </div>
        </div>

        <div className="md:col-span-3 flex items-center gap-2">
          <code className="mono text-xs px-2.5 py-1.5 bg-cream-200 text-ink-900/85 rounded flex-1 truncate" data-testid={`prospect-url-${p.slug}`}>
            /p/{p.slug}
          </code>
          <button onClick={() => onCopy(url)} title="Copy URL" data-testid={`copy-url-${p.slug}`}
                  className="h-8 w-8 grid place-items-center rounded border border-ink-900/10 text-ink-900/65 hover:text-sun-500 hover:border-sun-500 transition-colors">
            <Copy className="h-3.5 w-3.5" />
          </button>
          <a href={url} target="_blank" rel="noreferrer" title="Open" data-testid={`open-url-${p.slug}`}
             className="h-8 w-8 grid place-items-center rounded border border-ink-900/10 text-ink-900/65 hover:text-sun-500 hover:border-sun-500 transition-colors">
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>

        <div className="md:col-span-3 flex items-center gap-2 min-w-0">
          <code className="mono text-sm px-3 py-1.5 bg-sun-500/12 border border-sun-500/30 text-sun-300 rounded tracking-[0.12em] truncate min-w-0" data-testid={`code-${p.slug}`}>
            {p.code}
          </code>
          <button onClick={() => onCopy(p.code)} title="Copy code" data-testid={`copy-code-${p.slug}`}
                  className="h-8 w-8 grid place-items-center rounded border border-ink-900/10 text-ink-900/65 hover:text-sun-500 hover:border-sun-500 transition-colors">
            <Copy className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => onChangeCode(p)} title="Change code" data-testid={`change-code-${p.slug}`}
                  className="h-8 w-8 grid place-items-center rounded border border-ink-900/10 text-ink-900/65 hover:text-sun-500 hover:border-sun-500 transition-colors">
            <KeyRound className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="md:col-span-2 flex items-center justify-end gap-2">
          <button onClick={() => onSendLink(p)} title={p.contact_email ? `Email link to ${p.contact_email}` : "Add contact_email first"}
                  data-testid={`send-link-${p.slug}`}
                  disabled={!p.contact_email}
                  className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/70 hover:text-sun-500 transition-colors px-2.5 py-2 border border-ink-900/10 rounded disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-ink-900/70">
            <Send className="h-3.5 w-3.5" /> Send
          </button>
          <button onClick={() => nav(`/admin/prospects/${p.id}`)} title="Analytics" data-testid={`view-${p.slug}`}
                  className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/70 hover:text-sun-500 transition-colors px-2.5 py-2 border border-ink-900/10 rounded">
            <BarChart3 className="h-3.5 w-3.5" /> {p.view_count || 0}
          </button>
          <button onClick={() => onDelete(p.id, p.company_name)} title="Delete" data-testid={`delete-${p.slug}`}
                  className="h-8 w-8 grid place-items-center rounded border border-ink-900/10 text-ink-900/55 hover:text-red-400 hover:border-red-400/40 transition-colors">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </li>
  );
}
